import { DetailField } from "@/core/components/detail-field";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { getErrorMessage } from "@/core/api/errors";
import { useBusiness } from "@/core/business";
import { formatMoney } from "@/core/utils/money";
import { useBulkApproveSpiffs } from "@/spiffs/query";
import { GroupedPeriodSpiffs } from "@/spiffs/spiffs.d";

interface MarkAsPaidModalProps {
  periodId: string | null;
  groupedPeriod?: GroupedPeriodSpiffs;
  onClose: () => void;
}

export const MarkAsPaidModal = ({
  periodId,
  groupedPeriod,
  onClose,
}: MarkAsPaidModalProps) => {
  const { businessId } = useBusiness();
  const { mutate: bulkApprove, isPending } = useBulkApproveSpiffs(businessId);

  const handleMarkAsPaid = () => {
    if (!groupedPeriod || !periodId) return;

    // Filter out dismissed spiffs before marking as paid
    const spiffIds = groupedPeriod.spiffs
      .filter((s) => !s.dismissed_at)
      .map((s) => s.id);

    // Extract period dates from the period ID (format: "YYYY-MM-DD_YYYY-MM-DD")
    const parts = periodId.split("_");
    if (parts.length !== 2) {
      console.error("Invalid period ID format:", periodId);
      toast.error("Invalid period format. Please try again.");
      return;
    }
    const [startDate, endDate] = parts;

    bulkApprove(
      {
        spiffIds,
        payPeriodStartDate: startDate,
        payPeriodInclusiveEndDate: endDate,
      },
      {
        onSuccess: () => {
          toast.success("Period marked as paid");
          onClose();
        },
        onError: (error: unknown) => {
          toast.error("Failed to mark period as paid", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  return (
    <Dialog open={!!periodId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="gap-6" description="Mark Period as Paid">
        <DialogHeader>
          <DialogTitle>Mark Period as Paid?</DialogTitle>
          <Typography variant="bodyW2" textColor="tertiary" className="mt-2">
            This action cannot be undone
          </Typography>
        </DialogHeader>

        {groupedPeriod && (
          <Stack gap="md" className="px-6">
            <div className="flex flex-wrap gap-4">
              <DetailField
                label="Period"
                message={groupedPeriod.period.displayRange}
                fullWidth={false}
                className="min-w-0 flex-1"
              />
              <DetailField
                label="Spiffs"
                message={groupedPeriod.spiffs.length.toString()}
                fullWidth={false}
                className="min-w-0 flex-1"
              />
              <DetailField
                label="Total amount"
                message={formatMoney(groupedPeriod.totalAmount)}
                fullWidth={false}
                className="min-w-0 flex-1"
              />
            </div>

            <Stack gap="xs">
              <Typography variant="subtitleW3" textColor="tertiary">
                Important
              </Typography>
              <Typography>
                Mark this period as paid after these spiffs have been processed
                through payroll and paid out. <b>This action is permanent</b>{" "}
                and cannot be undone. Once marked as paid, you can only
                undismiss spiffs from this period.
              </Typography>
            </Stack>
          </Stack>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleMarkAsPaid} disabled={isPending}>
            {isPending ? "Marking as paid..." : "Mark as paid"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Backward compatibility export
export const LockPeriodModal = MarkAsPaidModal;
